/**
 * UI Store
 * Manages ephemeral UI state (modals, drawers, filters, view preferences)
 * Uses Zustand persist middleware to save filters and preferences to localStorage
 * 
 * Requirements: 3.2, 3.8
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CategoryLabel, SourceType } from '../types/api';

// Filter types
export interface KnowledgeFilters {
  source_type?: SourceType;
  category_label?: CategoryLabel;
  tag?: string;
  time_range?: 'today' | 'week' | 'month' | 'all';
}

export interface TaskFilters {
  status?: 'todo' | 'in_progress' | 'done' | 'snoozed';
  priority?: 'high' | 'medium' | 'low';
  tag?: string;
}

interface UIState {
  // Modal/Drawer state (not persisted)
  isDetailDrawerOpen: boolean;
  detailDrawerId: string | null;
  isModalOpen: boolean;
  modalType: 'capture' | 'quick-ask' | null;
  captureInitialTab: 'text' | 'file' | 'url' | 'notes' | 'voice';
  captureVoiceRequestId: number;

  // App-wide wake-word state. Only `wakeWordEnabled` is persisted; a fresh
  // browser session still requires a user gesture before opening the mic.
  wakeWordEnabled: boolean;
  wakeWordSessionActivated: boolean;
  wakeWordListening: boolean;
  wakeWordStatus: 'idle' | 'starting' | 'listening' | 'paused' | 'blocked' | 'unsupported' | 'error';
  wakeWordError: string | null;
  voiceCaptureBusy: boolean;

  // Nav drawer (ephemeral)
  isNavDrawerOpen: boolean;
  navDrawerTab: 'chats' | 'browse';

  // Filter state (persisted)
  knowledgeFilters: KnowledgeFilters;
  taskFilters: TaskFilters;

  // View preferences (persisted)
  connectionViewMode: 'graph' | 'list';
  showCompletedTasks: boolean;
  sidebarCollapsed: boolean;
  theme: 'light' | 'dark' | 'system';

  // Actions
  openDetailDrawer: (id: string) => void;
  closeDetailDrawer: () => void;
  openModal: (type: 'capture' | 'quick-ask') => void;
  openVoiceCapture: () => void;
  closeModal: () => void;
  setWakeWordEnabled: (enabled: boolean) => void;
  activateWakeWordSession: () => void;
  setWakeWordRuntime: (runtime: {
    listening: boolean;
    status: UIState['wakeWordStatus'];
    error?: string | null;
  }) => void;
  setVoiceCaptureBusy: (busy: boolean) => void;
  openNavDrawer: (tab?: 'chats' | 'browse') => void;
  closeNavDrawer: () => void;
  setNavDrawerTab: (tab: 'chats' | 'browse') => void;
  setKnowledgeFilters: (filters: Partial<KnowledgeFilters>) => void;
  setTaskFilters: (filters: Partial<TaskFilters>) => void;
  toggleConnectionViewMode: () => void;
  toggleShowCompletedTasks: () => void;
  toggleSidebar: () => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      // Initial state - Modal/Drawer (ephemeral)
      isDetailDrawerOpen: false,
      detailDrawerId: null,
      isModalOpen: false,
      modalType: null,
      captureInitialTab: 'notes' as const,
      captureVoiceRequestId: 0,
      wakeWordEnabled: false,
      wakeWordSessionActivated: false,
      wakeWordListening: false,
      wakeWordStatus: 'idle' as const,
      wakeWordError: null,
      voiceCaptureBusy: false,
      isNavDrawerOpen: false,
      navDrawerTab: 'chats' as const,

      // Initial state - Filters (persisted)
      knowledgeFilters: {},
      taskFilters: {},

      // Initial state - View preferences (persisted)
      connectionViewMode: 'graph',
      showCompletedTasks: false,
      sidebarCollapsed: false,
      theme: 'system',

      // Actions - Modal/Drawer
      openDetailDrawer: (id) =>
        set({ isDetailDrawerOpen: true, detailDrawerId: id }),
      
      closeDetailDrawer: () =>
        set({ isDetailDrawerOpen: false, detailDrawerId: null }),
      
      openModal: (type) =>
        set({ isModalOpen: true, modalType: type, captureInitialTab: 'notes' }),

      openVoiceCapture: () =>
        set((state) => ({
          isModalOpen: true,
          modalType: 'capture',
          captureInitialTab: 'voice',
          captureVoiceRequestId: state.captureVoiceRequestId + 1,
          // Release the app-wide speech recognizer before MediaRecorder asks
          // for the same microphone. VoiceCapture clears this after the
          // recording/upload lifecycle finishes.
          voiceCaptureBusy: true,
        })),
      
      closeModal: () =>
        set({ isModalOpen: false, modalType: null, voiceCaptureBusy: false }),

      setWakeWordEnabled: (enabled) =>
        set({
          wakeWordEnabled: enabled,
          wakeWordSessionActivated: enabled,
          wakeWordListening: false,
          wakeWordStatus: enabled ? 'starting' : 'idle',
          wakeWordError: null,
        }),

      activateWakeWordSession: () =>
        set((state) => ({
          wakeWordSessionActivated: state.wakeWordEnabled,
          wakeWordStatus: state.wakeWordEnabled ? 'starting' : state.wakeWordStatus,
          wakeWordError: null,
        })),

      setWakeWordRuntime: ({ listening, status, error = null }) =>
        set({ wakeWordListening: listening, wakeWordStatus: status, wakeWordError: error }),

      setVoiceCaptureBusy: (busy) => set({ voiceCaptureBusy: busy }),

      openNavDrawer: (tab) =>
        set((state) => ({
          isNavDrawerOpen: true,
          navDrawerTab: tab ?? state.navDrawerTab,
        })),

      closeNavDrawer: () => set({ isNavDrawerOpen: false }),

      setNavDrawerTab: (tab) => set({ navDrawerTab: tab }),

      // Actions - Filters
      setKnowledgeFilters: (filters) =>
        set((state) => ({
          knowledgeFilters: { ...state.knowledgeFilters, ...filters },
        })),
      
      setTaskFilters: (filters) =>
        set((state) => ({
          taskFilters: { ...state.taskFilters, ...filters },
        })),

      // Actions - View preferences
      toggleConnectionViewMode: () =>
        set((state) => ({
          connectionViewMode: state.connectionViewMode === 'graph' ? 'list' : 'graph',
        })),
      
      toggleShowCompletedTasks: () =>
        set((state) => ({
          showCompletedTasks: !state.showCompletedTasks,
        })),
      
      toggleSidebar: () =>
        set((state) => ({
          sidebarCollapsed: !state.sidebarCollapsed,
        })),

      setTheme: (theme) =>
        set({ theme }),
    }),
    {
      name: 'second-brain-ui', // localStorage key
      // Only persist filters and preferences, NOT modal/drawer state
      partialize: (state) => ({
        knowledgeFilters: state.knowledgeFilters,
        taskFilters: state.taskFilters,
        connectionViewMode: state.connectionViewMode,
        showCompletedTasks: state.showCompletedTasks,
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
        wakeWordEnabled: state.wakeWordEnabled,
      }),
    }
  )
);
